import type { MemberView, MembershipView } from '#src/generated/protocol';

import { profileOverrides } from '#lib/profile/profile-overrides.svelte.js';

export type MemberSort = 'name-asc' | 'name-desc' | 'newest' | 'oldest';
export type MembershipFilter = 'join' | 'invite' | 'knock' | 'leave' | 'kick' | 'ban';

export const MEMBER_SORTS: readonly MemberSort[] = ['name-asc', 'name-desc', 'newest', 'oldest'];
export const MEMBERSHIP_FILTERS: readonly MembershipFilter[] = [
  'join',
  'invite',
  'knock',
  'leave',
  'kick',
  'ban',
];

export const INITIAL_MEMBER_ROWS = 30;
export const DEFAULT_ALWAYS_LISTED_FROM = 1;
export const MEMBER_ROWS_STEP = 50;

export const MEMBER_SORT_LABELS: Record<MemberSort, string> = {
  'name-asc': 'common.sortNameAsc',
  'name-desc': 'common.sortNameDesc',
  newest: 'timeline.memberSortNewest',
  oldest: 'timeline.memberSortOldest',
};

export const MEMBERSHIP_FILTER_LABELS: Record<MembershipFilter, string> = {
  join: 'timeline.memberFilterJoined',
  invite: 'timeline.memberFilterInvited',
  knock: 'room.membersRequests',
  leave: 'timeline.memberFilterLeft',
  kick: 'timeline.memberFilterKicked',
  ban: 'timeline.memberFilterBanned',
};

export function membershipFor(filter: MembershipFilter): MembershipView {
  return filter === 'kick' ? 'leave' : filter;
}

export function memberName(member: MemberView): string {
  return profileOverrides.name(member.user_id, member.display_name ?? member.user_id);
}

export function matchesFilter(member: MemberView, filter: MembershipFilter): boolean {
  if (member.membership !== membershipFor(filter)) return false;
  if (filter === 'leave') return !member.kicked;
  if (filter === 'kick') return member.kicked;
  return true;
}

function compare(sort: MemberSort): (left: MemberView, right: MemberView) => number {
  switch (sort) {
    case 'name-desc':
      return (left, right) => byName(right, left);
    case 'newest':
      return (left, right) => (right.member_ts ?? 0) - (left.member_ts ?? 0);
    case 'oldest':
      return (left, right) => (left.member_ts ?? 0) - (right.member_ts ?? 0);
    default:
      return byName;
  }
}

function byName(left: MemberView, right: MemberView): number {
  return memberName(left).localeCompare(memberName(right), undefined, { sensitivity: 'base' });
}

export interface MemberGroup {
  key: string;
  level: number | null;
  members: MemberView[];
}

export function groupMembers(
  members: readonly MemberView[],
  sort: MemberSort,
  isOnline: (userId: string) => boolean,
  alwaysListedFrom = DEFAULT_ALWAYS_LISTED_FROM
): MemberGroup[] {
  const ordered = [...members].sort(compare(sort));
  const listed = (member: MemberView): boolean =>
    member.power_level >= alwaysListedFrom || isOnline(member.user_id);
  const offline = ordered.filter((member) => !listed(member));
  const online = ordered.filter(listed).sort((left, right) => right.power_level - left.power_level);

  const groups: MemberGroup[] = [];
  for (const member of online) {
    const current = groups.at(-1);
    if (current && current.level === member.power_level) current.members.push(member);
    else
      groups.push({
        key: String(member.power_level),
        level: member.power_level,
        members: [member],
      });
  }
  if (offline.length > 0) groups.push({ key: 'offline', level: null, members: offline });
  return groups;
}

export function limitGroups(groups: readonly MemberGroup[], limit: number): MemberGroup[] {
  const limited: MemberGroup[] = [];
  let taken = 0;
  for (const group of groups) {
    if (taken >= limit) break;
    const members = group.members.slice(0, limit - taken);
    taken += members.length;
    limited.push({ ...group, members });
  }
  return limited;
}
